import { NavLink, useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext.js'
import { styled } from '@mui/material/styles'
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Chip
} from '@mui/material'
import { Logout } from '@mui/icons-material'
import { FORD_COLORS, FORD_TYPOGRAPHY, FORD_SPACING, FORD_SHADOWS, FORD_RADIUS } from '../theme/fordTheme.js';

// Navigation configuration array
const NAVIGATION_ITEMS = [
  {
    id: 'command-center',
    path: '/command-center',
    label: 'Command Center',
    end: true,
    requiresAuth: false,
    requiredRole: null
  },
  {
    id: 'dashboard',
    path: '/dashboard',
    label: 'Dashboard',
    end: true,
    requiresAuth: false,
    requiredRole: null
  },
  {
    id: 'configuration',
    path: '/configuration',
    label: 'Configuration',
    end: false,
    requiresAuth: false,
    requiredRole: null
  },
  {
    id: 'outage',
    path: '/outage',
    label: 'Outage Report',
    end: false,
    requiresAuth: false,
    requiredRole: null
  },
  {
    id: 'device-trouble',
    path: '/device-trouble-shooting',
    label: 'Device Troubleshooting',
    end: false,
    requiresAuth: true,
    requiredRole: 'cognizantUser'
  },
  {
    id: 'device-management',
    path: '/device-management',
    label: 'Device Mgmt',
    end: false,
    requiresAuth: false,
    requiredRole: null
  }
];

// Helper function to check if user can access navigation item
const canAccessNavItem = (item, user) => {
  if (!item.requiresAuth) return true;
  if (item.requiredRole === 'cognizantUser') {
    return user?.isCognizantUser;
  }
  return true;
};

// Styled components following Ford brand theme
const StyledAppBar = styled(AppBar)(({ theme }) => ({
  backgroundColor: '#0a2540',
  // borderBottom: `1px solid ${FORD_COLORS.primaryLight}`,
  boxShadow: FORD_SHADOWS.md,
  position: 'static',
}))

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  padding: `0 ${FORD_SPACING.lg}`,
  minHeight: '64px',
  [theme.breakpoints.down('lg')]: {
    padding: `0 ${FORD_SPACING.md}`,
    flexWrap: 'nowrap',
    gap: FORD_SPACING.xs,
  },
  [theme.breakpoints.down('md')]: {
    padding: `0 ${FORD_SPACING.sm}`,
    flexWrap: 'nowrap',
    gap: FORD_SPACING.xs,
  },
}))

const StyledBrand = styled(Box)(({ theme , isCheckForMaxNavItems }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: FORD_SPACING.xs,
  marginRight: isCheckForMaxNavItems ? '10px !important' : FORD_SPACING.sm,
  [theme.breakpoints.down('lg')]: {
    // marginRight: FORD_SPACING.md,
    gap: '4px',
  },
  [theme.breakpoints.down('md')]: {
    marginRight: FORD_SPACING.sm,
    gap: '3px',
  },
  [theme.breakpoints.down('sm')]: {
    marginRight: FORD_SPACING.xs,
    gap: '2px',
  },
}))

const StyledBrandText = styled(Typography)(({ theme }) => ({
  color: FORD_COLORS.primary,
  fontSize: FORD_TYPOGRAPHY.body1,
  fontWeight: FORD_TYPOGRAPHY.bold,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  textDecoration: 'none',
  letterSpacing: FORD_TYPOGRAPHY.letterSpacingWide,
  // [theme.breakpoints.down('lg')]: {
  //   fontSize: FORD_TYPOGRAPHY.h5,
  // },
  // [theme.breakpoints.down('md')]: {
  //   fontSize: FORD_TYPOGRAPHY.h6,
  // },
  // [theme.breakpoints.down('sm')]: {
  //   fontSize: FORD_TYPOGRAPHY.body1,
  // },
}))

const StyledLogo = styled('img')(({ theme }) => ({
  height: '40px',
  width: '60px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  [theme.breakpoints.down('lg')]: {
    height: '52px',
    maxWidth: '130px',
  },
  [theme.breakpoints.down('md')]: {
    height: '44px',
    maxWidth: '110px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '36px',
    maxWidth: '90px',
  },
}))

const StyledPowerMonitorLogo = styled('img')(({ theme }) => ({
  height: '40px',
  width: '40px',
  maxWidth: '120px',
  cursor: 'pointer',
  transition: 'transform 0.2s ease-in-out',
  '&:hover': {
    transform: 'scale(1.05)',
  },
  [theme.breakpoints.down('lg')]: {
    height: '42px',
    maxWidth: '100px',
  },
  [theme.breakpoints.down('md')]: {
    height: '36px',
    maxWidth: '85px',
  },
  [theme.breakpoints.down('sm')]: {
    height: '30px',
    maxWidth: '70px',
  },
}))

const StyledNavTabs = styled(Box)(({ theme, isCheckForMaxNavItems }) => ({
  display: 'flex',
  gap: isCheckForMaxNavItems ? FORD_SPACING.xs : FORD_SPACING.sm,
  alignItems: 'center',
  [theme.breakpoints.down('lg')]: {
    gap: FORD_SPACING.xs,
  },
  [theme.breakpoints.down('md')]: {
    gap: isCheckForMaxNavItems ? '2px' : FORD_SPACING.xs,
  },
}))

const StyledNavLink = styled(NavLink)(({ theme, isCheckForMaxNavItems }) => ({
  textDecoration: 'none',
  padding: `${FORD_SPACING.sm} ${isCheckForMaxNavItems ? '8px' : '10px'}`,
  borderRadius: FORD_RADIUS.sm,
  color: 'rgba(255, 255, 255, 0.6)',
  fontSize: isCheckForMaxNavItems ? '0.75rem' : FORD_TYPOGRAPHY.body2,
  fontWeight: FORD_TYPOGRAPHY.medium,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  transition: 'background-color 0.15s ease, color 0.15s ease',
  display: 'inline-block',
  whiteSpace: 'nowrap',
  [theme.breakpoints.down('md')]: {
    padding: `${FORD_SPACING.sm} ${isCheckForMaxNavItems ? '6px' : '8px'}`,
    fontSize: isCheckForMaxNavItems ? '0.625rem' : '0.75rem',
  },
  '&:hover': {
    backgroundColor: `${FORD_COLORS.primaryLight}20`,
    // transform: 'translateY(-1px)', // Removed to prevent flickering
  },
  '&.active': {
    backgroundColor: '#ffffff29',
    boxShadow: FORD_SHADOWS.sm,
    color: FORD_COLORS.textColorWhite,
    fontFamily: FORD_TYPOGRAPHY.fallbackFont,
    fontWeight: '700',
    fontSize: '14px'
  },
  [theme.breakpoints.down('lg')]: {
    padding: `${FORD_SPACING.xs} ${FORD_SPACING.sm}`,
    fontSize: FORD_TYPOGRAPHY.caption,
  },
  [theme.breakpoints.down('md')]: {
    padding: `${FORD_SPACING.xs} ${FORD_SPACING.xs}`,
    fontSize: '0.7rem',
  },
}))

const StyledUserSection = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: FORD_SPACING.sx,
  flexShrink: 0,
  [theme.breakpoints.down('lg')]: {
    gap: FORD_SPACING.sm,
  },
  [theme.breakpoints.down('md')]: {
    gap: FORD_SPACING.xs,
  },
}))

const StyledUserEmail = styled(Chip)(({ theme }) => ({
  // backgroundColor: `${FORD_COLORS.surfaceDark}80`,
  color: FORD_COLORS.primary,
  // border: `1px solid ${FORD_COLORS.primaryLight}`,
  fontSize: FORD_TYPOGRAPHY.caption,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  marginLeft: '10px',
  '& .MuiChip-label': {
    padding: `0 ${FORD_SPACING.md}`,
    // fontWeight: FORD_TYPOGRAPHY.medium,
  },
  [theme.breakpoints.down('lg')]: {
    fontSize: FORD_TYPOGRAPHY.caption,
    '& .MuiChip-label': {
      padding: `0 ${FORD_SPACING.sm}`,
    },
  },
  [theme.breakpoints.down('md')]: {
    fontSize: '0.7rem',
    '& .MuiChip-label': {
      padding: `0 ${FORD_SPACING.xs}`,
    },
  },
}))

const StyledLogoutButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  color: FORD_COLORS.primary,
  textTransform: 'none',
  borderRadius: FORD_RADIUS.sm,
  padding: `${FORD_SPACING.sm} ${FORD_SPACING.md}`,
  fontWeight: FORD_TYPOGRAPHY.medium,
  fontFamily: FORD_TYPOGRAPHY.fallbackFont,
  fontSize: FORD_TYPOGRAPHY.body2,
  // border: `1px solid ${FORD_COLORS.primaryLight}`,
  transition: 'background-color 0.15s ease, color 0.15s ease',
  '&:hover': {
    backgroundColor: `${FORD_COLORS.primaryLight}20`,
    // borderColor: FORD_COLORS.error,
    // color: FORD_COLORS.error,
    // transform: 'translateY(-1px)', // Removed to prevent flickering
  },
  [theme.breakpoints.down('lg')]: {
    padding: `${FORD_SPACING.xs} ${FORD_SPACING.sm}`,
    fontSize: FORD_TYPOGRAPHY.caption,
    minWidth: 'auto',
  },
  [theme.breakpoints.down('md')]: {
    padding: `${FORD_SPACING.xs} ${FORD_SPACING.xs}`,
    fontSize: '0.7rem',
    minWidth: 'auto',
  },
}))

export default function NavTabs() {
  const { user, logout } = useAuth()
  const navigate = useNavigate()

  const handleLogout = () => {
    logout()
    navigate('/login', { replace: true })
  }

  const FINAL_NAVIGATION_ITEMS = NAVIGATION_ITEMS.filter(item => canAccessNavItem(item, user));
  const isCheckForMaxNavItems = FINAL_NAVIGATION_ITEMS.length === 6;

  return (
    <StyledAppBar>
      <StyledToolbar>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <StyledBrand isCheckForMaxNavItems={isCheckForMaxNavItems}>
            <StyledLogo
              src="/ford-logo.png"
              alt="Ford Logo"
              onClick={() => navigate('/command-center')}
            />
            <StyledPowerMonitorLogo
              src="/powermonitor.png"
              alt="Power Monitor Logo"
              onClick={() => navigate('/command-center')}
            />
            <StyledBrandText variant="h6" component="div">
              Power Monitor <br /> Application
            </StyledBrandText>
          </StyledBrand>

          <StyledNavTabs isCheckForMaxNavItems={isCheckForMaxNavItems}>
            {FINAL_NAVIGATION_ITEMS.map(item => (
                <StyledNavLink
                  key={item.id}
                  to={item.path}
                  end={item.end}
                  isCheckForMaxNavItems={isCheckForMaxNavItems}
                  className={({ isActive }) => isActive ? 'active' : ''}
                >
                  {item.label}
                </StyledNavLink>
              ))
            }
          </StyledNavTabs>
        </Box>

        <StyledUserSection>
          <StyledUserEmail
            label={user?.email || 'User'}
            size="small"
          />
          <StyledLogoutButton
            onClick={handleLogout}
            startIcon={<Logout />}
          >
            Log out
          </StyledLogoutButton>
        </StyledUserSection>
      </StyledToolbar>
    </StyledAppBar>
  )
}
